import React from "react";

/**
 * InvoiceScroll — central 3D object on the hero. A cream parchment with a
 * stamped wax seal. Bobs gently. On hover (desktop) it rotates slightly
 * around the Y axis to show depth.
 *
 * Built entirely with CSS 3D transforms — no Three.js, no R3F, no Lottie.
 * Lightweight enough for mobile.
 */
const InvoiceScroll = () => {
  return (
    <div
      data-testid="invoice-scroll"
      className="relative l2-tilt l2-bob l2-float-shadow"
      style={{
        width: "clamp(220px, 26vw, 340px)",
        height: "clamp(280px, 32vw, 420px)",
        transform: "rotateX(8deg) rotateY(-6deg) rotateZ(-2deg)",
      }}
    >
      {/* Paper sheet — main face */}
      <div
        className="absolute inset-0 rounded-[20px] flex flex-col p-5 md:p-7"
        style={{
          background:
            "linear-gradient(160deg, var(--l2-paper) 0%, #F4ECDB 100%)",
          boxShadow:
            "0 30px 60px -20px rgba(31, 27, 23, 0.25), 0 8px 20px -8px rgba(31, 27, 23, 0.15), inset 0 0 0 1px rgba(31, 27, 23, 0.06)",
        }}
      >
        {/* Header — INVOICE wordmark */}
        <div className="flex items-start justify-between">
          <div>
            <div
              className="l2-display text-xl md:text-2xl"
              style={{ color: "var(--l2-ink)" }}
            >
              INVOICE
            </div>
            <div
              className="l2-mono text-[10px] md:text-[11px] mt-0.5"
              style={{ color: "var(--l2-ink-2)" }}
            >
              #QLL-2026-0042
            </div>
          </div>
          <div
            className="l2-mono text-[10px] md:text-[11px] px-2 py-1 rounded-full"
            style={{
              background: "var(--l2-mint)",
              color: "var(--l2-ink)",
            }}
          >
            PAID
          </div>
        </div>

        {/* Line items — visual sketch */}
        <div className="mt-4 md:mt-5 space-y-2 flex-1">
          {[
            { label: "Sponsored video — main feed", amt: "$1,800.00" },
            { label: "Affiliate links integration", amt: "$420.00" },
            { label: "Usage rights extension",     amt: "$280.00" },
          ].map((r) => (
            <div
              key={r.label}
              className="flex items-center justify-between text-[11px] md:text-xs"
            >
              <span style={{ color: "var(--l2-ink-2)" }}>{r.label}</span>
              <span
                className="l2-mono"
                style={{ color: "var(--l2-ink)" }}
              >
                {r.amt}
              </span>
            </div>
          ))}
        </div>

        {/* Total */}
        <div
          className="mt-3 md:mt-4 pt-3 border-t flex items-center justify-between"
          style={{ borderColor: "rgba(31,27,23,0.12)" }}
        >
          <span
            className="l2-mono text-[10px] md:text-[11px] uppercase tracking-wider"
            style={{ color: "var(--l2-ink-2)" }}
          >
            Total
          </span>
          <span
            className="l2-display text-lg md:text-xl"
            style={{ color: "var(--l2-ink)" }}
          >
            $2,500.00
          </span>
        </div>

        {/* Wax seal — bottom right, slightly overhanging */}
        <div
          className="absolute l2-seal"
          style={{
            right: "-14px",
            bottom: "-14px",
            width: "clamp(72px, 9vw, 112px)",
            height: "clamp(72px, 9vw, 112px)",
            transform: "translateZ(20px)",
          }}
          aria-hidden
        >
          <div
            className="w-full h-full rounded-full relative"
            style={{
              background:
                "radial-gradient(circle at 35% 30%, #D9543F 0%, var(--l2-wax) 50%, #7A2418 100%)",
              boxShadow:
                "0 8px 20px -4px rgba(178, 58, 42, 0.5), inset 2px 2px 6px rgba(255,255,255,0.25), inset -3px -3px 8px rgba(0,0,0,0.35)",
            }}
          >
            <div
              className="absolute inset-0 flex items-center justify-center l2-display"
              style={{
                color: "rgba(255, 246, 229, 0.95)",
                fontSize: "clamp(28px, 3.6vw, 44px)",
                textShadow: "0 2px 0 rgba(0,0,0,0.25)",
              }}
            >
              Q
            </div>
            {/* Wax drip */}
            <div
              className="absolute"
              style={{
                top: "-4px",
                left: "30%",
                width: "20%",
                height: "30%",
                background:
                  "radial-gradient(ellipse, var(--l2-wax) 0%, transparent 70%)",
                opacity: 0.7,
              }}
            />
          </div>
        </div>

        {/* "Signed with Quill" footer ink stroke */}
        <div className="mt-3 flex items-center justify-between">
          <svg width="80" height="20" viewBox="0 0 80 20">
            <path
              d="M2 14 Q 10 4, 22 12 T 45 10 T 70 14"
              stroke="var(--l2-ink)"
              strokeWidth="1.6"
              fill="none"
              strokeLinecap="round"
            />
          </svg>
          <span
            className="l2-mono text-[9px] md:text-[10px]"
            style={{ color: "var(--l2-ink-2)" }}
          >
            Drafted with Quill
          </span>
        </div>
      </div>

      {/* Slight back-face shadow for depth */}
      <div
        aria-hidden
        className="absolute inset-0 rounded-[20px] -z-10"
        style={{
          background: "rgba(31, 27, 23, 0.08)",
          transform: "translate3d(4px, 6px, -10px)",
          filter: "blur(2px)",
        }}
      />
    </div>
  );
};

export default InvoiceScroll;
